import SETTINGS from '@verzekeringapp-mono/shared/src/constants/settings'
import { useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import Button from '~/components/Button'
import TextInput from '~/components/TextInput'
import { useEmergencyModalContext } from '~/providers/EmergencyModalProvider'
import { getAgencyPrintout } from '@verzekeringapp-mono/shared/src/state/selectors/agency'
import { Images } from '@verzekeringapp-mono/shared/src/assets'
import LoginScreenWrapper from '~/components/screen_wrappers/login_screen_wrapper'
import useAuthentication from '@verzekeringapp-mono/shared/src/hooks/use_authentication'
import Modal from '~/components/Modal'

const RegisterScreen = () => {
  const { state } = useLocation()
  const { open } = useEmergencyModalContext()
  const  {
    control,
    handleSubmit,
    formState: { isValid }
  } = useForm({
    defaultValues: {
      email: state?.email || ''
    }
  })
  const navigate = useNavigate()
  const { registerEmail } = useAuthentication()

  const [isLoading, setIsLoading] = useState(false)

  const agencyPrintout = useAppSelector(getAgencyPrintout)
  const [error, setError] = useState<string|null>(null)
  const [successMessage, setSuccessMessage] = useState<string|null>(null)

  const logo = useMemo(() => {
    if (agencyPrintout?.logoUrl) {
      return `${agencyPrintout.logoUrl}?${Date.now()}`
    }

    return Images.Logo
  }, [agencyPrintout])

  const onSubmit = async (input: { email: string }) => {
    const { email } = input

    setIsLoading(true)

    try {

      const { message } = await registerEmail(email)

      setSuccessMessage(message ?? 'Er is een e-mail verzonden met instructies om je account te registreren.')
    } catch (error) {
      setError((error as Error).message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <LoginScreenWrapper>
      <img
        src={logo}
        width={180}
        height={70}
        alt={'Logo van jouw verzekeringsadviseur'}
      />

      <h1 className='title-medium mt-8'>{'Welkom'}</h1>

      <h2 className='description my-8'>
      In Mijn Polissen kun je snel schademelden en heb je toegang tot je dossier, verzekeringsbewijs, polissen en meer. Begin hier met registreren.
      </h2>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          render={({ field, fieldState }) => (
            <TextInput
              {...field}
              label='E-mailadres'
              placeholder='Je e-mailadres'
              showValidationIcon={false}
              isValid={!fieldState.invalid}
              icon={'envelope'}
            />
          )}
          name='email'
          control={control}
          rules={{
            required: 'Dit veld is verplicht',
            pattern: {
              value: /^(?!\.)(?!.*\.\.)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~.-]+(?<!\.)@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/,
              message: 'Dit is geen geldig e-mailadres'
            }
          }}
        />

        {error && (
          <span
            className='labelError mt-4 whitespace-pre-line'
            dangerouslySetInnerHTML={{ __html: error }}
          />
        )}

        <Button
          className={`primary-btn w-full ${isValid ? '' : 'opacity-50'} my-5`}
          type='submit'
          isLoading={isLoading}
          disabled={!isValid}
        >
          {'Verder'}
        </Button>
      </form>

      <Button
        className='w-full my-2'
        onClick={() => navigate('/home')}
        variant='outline'
        type='button'
      >
      Terug
      </Button>

      <p className='mt-3'>
      In geval van nood, neem contact op via het noodnummer: <br />

        <a
          onClick={open}
          className='text-status-danger-foreground'
        >
          {SETTINGS.emergencyPhoneNumber}
        </a>
      </p>

      {successMessage && (
        <Modal onClose={() => navigate('/home')}>
          <p>{successMessage}</p>
        </Modal>
      )}
    </LoginScreenWrapper>
  )
}

export default RegisterScreen
